import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container, Section, SectionHeader, Kicker, PullQuote } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Photo } from '../components/ui/Photo';
import { PHOTOS } from '../data/photos';
import { ExplainerVideo } from '../components/sections/ExplainerVideo';
import { METHOD_STEPS } from '../data/services';
export function Method() {
  const [active, setActive] = React.useState(0);
  const reduced = useReducedMotion();
  const step = METHOD_STEPS[active];
  return <>
      <section className="surface-dark" data-id="element-2764" data-mp="37p3d0dge6vxa">
        <Container className="grid gap-12 py-16 sm:py-24 lg:grid-cols-[1.2fr_0.8fr] lg:items-center" data-id="element-2765" data-mp="37q4nogthxgeh">
          <div data-id="element-2766" data-mp="3br81kxrzh2d2">
            <Kicker data-id="element-2767" data-mp="3bqoktke2kk0m">Augmented intelligence</Kicker>
            <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-2768" data-mp="2xq454x3gb4bc">
              Made for people, powered by AI
            </h1>
            <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-2769" data-mp="ehaveloj4l6n">
              Augmented intelligence is not automation with better marketing. It is the deliberate pairing of
              machine analysis with human judgement, arranged so that a person remains accountable for the decision.
              Four steps carry that principle into every engagement.
            </p>
          </div>
          <Photo photo={PHOTOS.workshopTable} aspect="aspect-[4/3] lg:aspect-[4/5]" tone="dark" eager data-id="element-2770" data-mp="ih39fdwozjje" />
        </Container>
      </section>

      <Section aria-labelledby="stepper" data-id="element-2771" data-mp="3mxxcfjr87pxe">
        <SectionHeader id="stepper" kicker="The method" title="Capture, apply, enable, decide" lead="Move through each step to see what it involves and what you receive." data-id="element-2772" data-mp="3mv1cjpvtir6b" />

        <div className="mt-12" data-id="element-2773" data-mp="38885f7wgcrp7">
          <ol role="tablist" aria-label="Method steps" className="grid gap-px overflow-hidden rounded-md bg-line sm:grid-cols-4" data-id="element-2774" data-mp="387gdc722dc3o">
            {METHOD_STEPS.map((item, index) => {
            const isActive = index === active;
            return <li key={item.id} data-id="element-2775" data-mp="1745yjnfpvvhd" data-mpi={index}>
                  <button type="button" role="tab" id={`step-tab-${item.id}`} aria-selected={isActive} aria-controls="step-panel" onClick={() => setActive(index)} className={`relative w-full px-5 py-6 text-left transition-colors ${isActive ? 'bg-gold text-charcoal' : 'bg-panel text-body hover:bg-mist'}`} data-id="element-2776" data-mp="1c4bkcmzb2nrj">
                    <span className={`font-display text-[13px] ${isActive ? 'text-gold' : 'text-muted'}`} data-id="element-2777" data-mp="y49578idu8fz">
                      0{index + 1}
                    </span>
                    <span className={`mt-2 block font-display text-[21px] ${isActive ? 'text-white' : 'text-ink'}`} data-id="element-2778" data-mp="29kr2fatt0f3o">
                      {item.title}
                    </span>
                  </button>
                </li>;
          })}
          </ol>

          <motion.div key={step.id} id="step-panel" role="tabpanel" aria-labelledby={`step-tab-${step.id}`} initial={reduced ? false : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="mt-8 grid gap-8 rounded-md bg-mist p-7 sm:p-10 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-2779" data-mp="20tnayx2kxen">
            <div data-id="element-2780" data-mp="3aesgxtwkk6oz">
              <h3 className="font-display text-[26px] leading-tight text-ink sm:text-[30px]" data-id="element-2781" data-mp="3ae9ab9cdd4q9">{step.summary}</h3>
              <p className="mt-5 max-w-prose text-[16px] text-body" data-id="element-2782" data-mp="36dytf1n1gtwm">{step.detail}</p>
            </div>
            <div className="border-l-2 border-gold pl-5" data-id="element-2783" data-mp="31f5s07l10tct">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-2784" data-mp="14lanulmx9udo">What you receive</p>
              <p className="mt-3 font-display text-[20px] leading-snug text-ink" data-id="element-2785" data-mp="zly449rcl3fb">{step.output}</p>
            </div>
          </motion.div>
        </div>
      </Section>

      <Section tone="mist" aria-labelledby="explainer" data-id="element-2786" data-mp="3sf5215ber6dt">
        <SectionHeader id="explainer" kicker="Watch" title="The method in motion" lead="The same four steps, animated end to end, from scattered evidence to a decision somebody owns." data-id="element-2787" data-mp="19ddgada41t4" />
        <div className="mt-10" data-id="element-2788" data-mp="3ohh19ln1aje0">
          <ExplainerVideo data-id="element-2789" data-mp="39qq7w95snc8s" />
        </div>
      </Section>

      <Section tone="champagne" data-id="element-2790" data-mp="39p5gutioubfo">
        <div className="grid gap-12 lg:grid-cols-2" data-id="element-2791" data-mp="3egwxocc1l2sm">
          <div data-id="element-2792" data-mp="3eiri7pjeisw6">
            <Kicker data-id="element-2793" data-mp="3ej9pribiktje">The ethos</Kicker>
            <h2 className="mt-4 font-display text-[28px] leading-tight text-ink sm:text-[34px]" data-id="element-2794" data-mp="30qbml9stxx5a">
              A person stays accountable for the decision
            </h2>
            <p className="mt-5 text-[16px] text-body" data-id="element-2795" data-mp="34p0w2raqyujn">
              Every system we build has a named reviewer, a documented route of appeal and a record of what the
              machine contributed. This is not a compliance gesture. It is what keeps a tool useful once the
              novelty passes and someone has to answer for an outcome.
            </p>
            <div className="mt-8" data-id="element-2796" data-mp="277dhloo3gae1">
              <Button to="/assessment" data-id="element-2797" data-mp="2786oqqqyt1wp">Test your readiness</Button>
            </div>
          </div>
          <PullQuote data-id="element-2798" data-mp="2m0qix1w4zz20">
            We would rather hand over a smaller system that your people trust than a larger one they quietly stop
            using.
          </PullQuote>
        </div>
      </Section>
    </>;
}